import {
  getServiceIdentityFromInput,
  toResourceAttributes,
  type ResourceAttributes,
  type ServiceIdentity,
  type ServiceIdentityInput,
} from "./shared-utils/serviceIdentity";

// Process identity for every runtime: Next Node and Edge, the browser, workers,
// crons. One rule set (shared-utils/serviceIdentity.ts); what differs per
// process is the configuration it starts with, so the server and the browser
// gather their inputs separately and neither falls back to the other's
// variables. Reads the process directly because it must resolve before any
// runtime-specific Zod env module is parsed. Rules: docs/service_identity.md.

export const WEB_SERVICE_NAME = "listen-labs-web";
export const BROWSER_SERVICE_NAME = "listen-labs-browser";

const serverInputs = (): ServiceIdentityInput => ({
  defaultServiceName: WEB_SERVICE_NAME,
  otelServiceName: process.env.OTEL_SERVICE_NAME,
  otelResourceAttributes: process.env.OTEL_RESOURCE_ATTRIBUTES,
  // Same fallback getDeploymentRegion() has always had on the server.
  deploymentRegion:
    process.env.DEPLOYMENT_REGION || process.env.NEXT_PUBLIC_DEPLOYMENT_REGION,
  vercelEnv: process.env.VERCEL_ENV,
  jobsEnv: process.env.JOBS_ENV,
  nodeEnv: process.env.NODE_ENV,
  renderExternalUrl: process.env.RENDER_EXTERNAL_URL,
  commitSha: process.env.VERCEL_GIT_COMMIT_SHA || process.env.RENDER_GIT_COMMIT,
});

// Only literally referenced NEXT_PUBLIC_* names exist in the browser bundle.
const browserInputs = (): ServiceIdentityInput => ({
  defaultServiceName: BROWSER_SERVICE_NAME,
  otelResourceAttributes: process.env.NEXT_PUBLIC_OTEL_RESOURCE_ATTRIBUTES,
  deploymentRegion: process.env.NEXT_PUBLIC_DEPLOYMENT_REGION,
  vercelEnv: process.env.NEXT_PUBLIC_VERCEL_ENV,
  commitSha: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
});

let _cached: ServiceIdentity | undefined;

export const getServiceIdentity = (): ServiceIdentity => {
  if (_cached) {
    return _cached;
  }
  const { identity, warnings } = getServiceIdentityFromInput(
    typeof window === "undefined" ? serverInputs() : browserInputs()
  );
  // Cache before warning: a console adapter may ask for the identity.
  _cached = identity;
  warnings.forEach((warning) =>
    console.warn(
      JSON.stringify({
        event: "service_identity.invalid_attribute",
        ...warning,
      })
    )
  );
  return _cached;
};

export const getIdentityResourceAttributes = (): ResourceAttributes =>
  toResourceAttributes(getServiceIdentity());
